/**
 * Format a RUT string for display: XX.XXX.XXX-X
 * Only formats when there are enough characters; avoids aggressive
 * reformatting that could cause cursor issues.
 */
export function formatRut(value: string): string {
  // Strip everything except digits and K
  const clean = value.replace(/[^0-9kK]/g, '').toUpperCase()
  if (clean.length === 0) return ''
  if (clean.length <= 1) return clean

  const body = clean.slice(0, -1)
  const dv = clean.slice(-1)

  // Add dots to body from right to left
  let formatted = ''
  for (let i = body.length - 1, count = 0; i >= 0; i--, count++) {
    formatted = body.charAt(i) + formatted
    if (count % 3 === 2 && i !== 0) {
      formatted = '.' + formatted
    }
  }

  return `${formatted}-${dv}`
}

/**
 * Validate a Chilean RUT using the official modulo 11 algorithm.
 *
 * Accepts any common format:
 *   12.345.678-5 | 12345678-5 | 123456785 | 12.345.678-K
 */
export function validateRut(rut: string): boolean {
  if (!rut || rut.trim().length < 2) return false

  // 1. Normalize: remove dots, spaces, uppercase
  let clean = rut.trim().replace(/\./g, '').replace(/\s/g, '').toUpperCase()

  // 2. Separate body and DV
  let body: string
  let dv: string

  if (clean.includes('-')) {
    const parts = clean.split('-')
    if (parts.length !== 2) return false
    body = parts[0]
    dv = parts[1]
  } else {
    // No hyphen: last char is DV, rest is body
    body = clean.slice(0, -1)
    dv = clean.slice(-1)
  }

  // 3. Validate structure
  if (!/^\d+$/.test(body)) return false
  if (!/^[0-9K]$/.test(dv)) return false
  if (body.length < 6 || body.length > 8) return false

  // 4. Compute expected DV with modulo 11
  const digits = body.split('').reverse()
  const multipliers = [2, 3, 4, 5, 6, 7]
  let sum = 0

  for (let i = 0; i < digits.length; i++) {
    sum += parseInt(digits[i], 10) * multipliers[i % 6]
  }

  const remainder = 11 - (sum % 11)

  let expectedDv: string
  if (remainder === 11) {
    expectedDv = '0'
  } else if (remainder === 10) {
    expectedDv = 'K'
  } else {
    expectedDv = remainder.toString()
  }

  // 5. Compare
  return dv === expectedDv
}
